<template>
  <tabs class="!py-5">
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/drivers/segmentation/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.drivers')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
    <action
      :icon="AddIcon"
      :label="$t('pages.segmentedDrivers.modal.edit.buttonText')"
      @click="openSegmentationModal = true"
    />
  </toolbar>
  <segmentation-modal
    v-model:open="openSegmentationModal"
    @saved="handleUpdate"
  />
  <div
    v-if="!isSegmented"
    class="flex justify-center"
    style="padding: 12rem;"
  >
    <Alert
      variant="info"
      :title="$t('pages.segmentedDrivers.extraFiltersPlaceholderTitle')"
      :sub-title="$t('pages.segmentedDrivers.extraFiltersPlaceholderGeneral')"
    />
  </div>
  <data-table
    v-else
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(s: SegmentedDriver) => s.driver?.id ? ({ name: 'driver-details', params: { driverId: s.driver.id } }) : ({ name: 'drivers' })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :disableFrontendSorting="true"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { computed, reactive, ref, unref, watch } from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
  collOptionsProvider,
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter} from '@/components/utils/filter/Filter.vue';
import type {
  Address,
  Campaign,
  Company,
  Driver,
  HydraCollection,
  Region,
  Segmentation, SegmentedDriver
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import Switch from '@utils/inputs/Switch.vue';
import {useTableFields} from '@hooks/table-configuration';
import AddIcon from '@images/panes/add.svg';
import Action from '@utils/toolbar/Action.vue';
import SegmentationModal from '@pages/drivers/SegmentationModal.vue';
import Alert from '@utils/Alert.vue';
import { formatNumberForCurrency, formatTimeDurationFromSeconds } from '@/utils/helpers';
import { useRoute, useRouter } from 'vue-router';
import type { FilterType } from '@utils/filter/filterTypes';

const hideDeleted = ref(true);
const openSegmentationModal = ref(false);
const editedSegmentation = ref<Segmentation | null>();
const isSegmented = computed(() => !!editedSegmentation.value);

const route = useRoute();
const router = useRouter();

if (route.query.segmentation) {
  try {
    editedSegmentation.value = JSON.parse(decodeURIComponent(String(route.query.segmentation)));
  } catch (e) {
    console.warn('Failed to parse segmentation from URL', e);
  }
}

// update URL when editedSegmentation changes, so it can be copied
watch(editedSegmentation, (newVal) => {
  router.replace({
    query: {
      ...route.query,
      segmentation: newVal ? encodeURIComponent(JSON.stringify(newVal)) : undefined,
    },
  });
}, { deep: true });

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<FilterType>('filter', {});

const totalFilter = computed(() => {
  return {
    'user.deleted': hideDeleted.value ? false : undefined,
    ...editedSegmentation.value,
    ...filter.value,
  };
});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

function handleUpdate(segmentation: Segmentation) {
  editedSegmentation.value = segmentation;
}

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'driver.id',
    title: t('id'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: true,
  },
  {
    key: 'startMatches',
    title: t('pages.segmentedDrivers.startMatches'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: false,
  },
  {
    key: 'endMatches',
    title: t('pages.segmentedDrivers.endMatches'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: false,
  },
  {
    key: 'driver.user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    sortable: false,
    sortKey: 'user.salutation',
    title: t('driver.title'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: (v: string) => t(`title.${v ?? 'unknown'}`),
    filter: {
      key: 'user.salutation',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    visible: true,
    sortable: false,
    group: t('pages.driverList.fieldGroups.general'),
    filter: {
      key: 'user.firstName',
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: true,
    filter: {
      key: 'user.lastName',
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'driver.region.name',
    title: t('user.region'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: true,
    sortKey: 'region.name'
  },
  {
    key: 'driver.user.email',
    title: t('user.email'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    filter: {
      key: 'user.email',
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'driver.user.birthday',
    title: t('user.birthday'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : ''),
    filter: {
      key: 'user.birthday',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.birthday'),
      },
    },
  },
  {
    key: 'driver.user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    filter: {
      key: 'user.phoneNumber',
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'driver.user.language',
    title: t('user.language'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    filter: {
      key: 'user.language',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/language', (v) => ({
          value: v,
          label: t(`language.${v}`),
        })),
        label: t('user.language'),
      },
    },
  },
  {
    key: 'driver.user.active',
    title: t('user.active'),
    csvTranslations: {
      true: t('user.isActive.true'),
      false: t('user.isActive.false'),
    },
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (active: boolean) => t(`user.isActive.${active}`),
    filter: {
      key: 'user.active',
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.active'),
        labelTrue: t('user.isActive.true'),
        labelFalse: t('user.isActive.false'),
      },
    },
  },
  {
    key: 'driver.driverType',
    title: t('driver.driverType.DEFAULT'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      key: 'driverType',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverType.DEFAULT'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'driver.driverTypeAtRegistration',
    title: t('driver.driverTypeAtRegistration'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      key: 'driverTypeAtRegistration',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverTypeAtRegistration'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'driver.profileComplete',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.profileComplete'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'profileComplete',
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.profileComplete'),
      },
    },
  },
  {
    key: 'driver.liabilityDisclaimerSigned',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.liabilityDisclaimerSigned'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'liabilityDisclaimerSigned',
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.liabilityDisclaimerSigned'),
      },
    },
  },
  {
    key: 'driver.user.emailVerifiedOn',
    title: t('user.emailVerified'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: string | null) => v ? t('yes') : t('no'),
    filter: {
      key: 'exists[user.emailVerifiedOn]',
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.emailVerified'),
      },
    },
  },
  {
    key: 'driver.warningLevel',
    title: t('driver.warningLevel.DEFAULT'),
    csvTranslations: {
      none: t('driver.warningLevel.none'),
      first: t('driver.warningLevel.first'),
      second: t('driver.warningLevel.second'),
      third: t('driver.warningLevel.third'),
    },
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (level: string) => t(`driver.warningLevel.${level}`),
    filter: {
      key: 'warningLevel',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/WarningLevel', (v) => ({
          value: v,
          label: t(`driver.warningLevel.${v}`),
        })),
        label: t('driver.warningLevel.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.noticeComment',
    title: t('driver.noticeComment.DEFAULT'),
    csvTranslations: {
      null: t('driver.noticeComment.null'),
      box_or_seal_problem: t('driver.noticeComment.box_or_seal_problem'),
      spare_part_needed: t('driver.noticeComment.spare_part_needed'),
      phase_3: t('driver.noticeComment.phase_3'),
      profile_wrong: t('driver.noticeComment.profile_wrong'),
      other: t('driver.noticeComment.other'),
    },
    group: t('pages.driverList.fieldGroups.care'),
    formatter: (v?: string) => v ? t(`driver.noticeComment.${v}`) : '',
    sortable: false,
    visible: true,
    filter: {
      key: 'noticeComment',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.care'),
      options: {
        options: enumOptionsProvider('/enums/DriverNoticeComment', (v) => ({
          value: v,
          label: t(`driver.noticeComment.${v}`),
        })),
        label: t('driver.noticeComment.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.caredForAt',
    title: t('driver.caredForAt'),
    group: t('pages.driverList.fieldGroups.care'),
    sortable: false,
    visible: true,
    formatter: (v: Date | null) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    filter: {
      key: 'caredForAt',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.care'),
      options: {
        label: t('driver.caredForAt'),
      },
    },
  },
  {
    key: 'driver.user.address.street',
    title: t('address.street'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: false,
    filter: {
      key: 'user.address.street',
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.street'),
      },
    },
  },
  {
    key: 'driver.user.address.cityName',
    title: t('address.city'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: false,
    filter: {
      key: 'user.address.cityName',
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.city'),
      },
    },
  },
  {
    key: 'driver.user.address.zip',
    title: t('address.zip'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: false,
    filter: {
      key: 'user.address.zip',
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.zip'),
      },
    },
  },
  {
    key: 'driver.box.status',
    csvTranslations: {
      null: t('box.status.noBox'),
      noBox: t('box.status.noBox'),
      Init: t('box.status.Init'),
      Mounted: t('box.status.Mounted'),
      Destroyed: t('box.status.Destroyed'),
      Other: t('box.status.Other'),
      Lost: t('box.status.Lost'),
      Returned: t('box.status.Returned'),
    },
    title: t('box.status.DEFAULT'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    formatter: (v: string) => v ? t(`box.status.${v}`) : t('box.status.noBox'),
    filter: {
      key: 'box.status',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxStatus', (item: string) => ({
            label: t(`box.status.${item}`),
            value: item,
          })), [
          { label: t('box.status.noBox'), value: 'noBox' }
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['box.status'];
            m['exists[box]'] = false;
          } else {
            delete m['exists[box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.status.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.type',
    title: t('box.type'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    filter: {
      key: 'box.type',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: enumOptionsProvider('/enums/BoxType', (item: string) => ({
          label: t(`box.types.${item}`),
          value: item,
        })),
        label: t('box.type'),
      },
    },
  },
  {
    key: 'driver.box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: t('box.variant.DEFAULT'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : ''),
    filter: {
      key: 'box.variant',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: enumOptionsProvider('/enums/BoxVariant', (item: string) => ({
          label: t(`box.variant.${item}`),
          value: item,
        })),
        label: t('box.variant.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.box.stable',
    title: `${t('box.stable')}`,
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    visible: true,
    formatter: (v: boolean|null) => v ? t('yes') : (v === false ? t('no') : t('unknown')),
    filter: {
      key: 'box.stable',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('unknown'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['box.stable'];
            m['exists[box.stable]'] = false;
          } else {
            delete m['exists[box.stable]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box.stable]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: `${t('box.stable')}`,
      },
    },
  },
  {
    key: 'driver.latestMountingTimeslotRegistration.timeslot.startTime',
    title: t('box.mountingAppointment'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      key: 'latestMountingTimeslotRegistration.timeslot.startTime',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.mountingAppointment'),
      },
    },
  },
  {
    key: 'driver.box.mountingDate',
    title: t('box.mountingDate'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy HH:mm:ss') : ''),
    filter: {
      key: 'box.mountingDate',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.mountingDate'),
      },
    },
  },
  {
    key: 'driver.box.firstCampaign.group.name',
    title: t('box.campaign'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: false,
    filter: {
      key: 'box.firstCampaign.group.name',
      type: 'text',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.campaign'),
      },
    },
  },
  {
    key: 'driver.specialDriverPhoto',
    title: t('driver.specialPhoto'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'specialDriverPhoto',
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.specialPhoto'),
      },
    },
  },
  {
    key: 'driver.specialDriverStanding',
    title: t('driver.specialStanding'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'specialDriverStanding',
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.specialStanding'),
      },
    }
  },
  {
    key: 'driver.helperDriver',
    title: t('driver.helper'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'helperDriver',
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.helper'),
      },
    },
  },
  {
    key: 'driver.defaultSourceAddress',
    title: `${t('driver.mostFrequentPath')} ${t('from')}`,
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: false,
    sortKey: ['defaultSourceAddress.street', 'defaultSourceAddress.cityName'],
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'defaultSourceAddress.street',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('from')}`,
              },
            },
            {
              key: 'defaultSourceAddress.zip',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'defaultSourceAddress.cityName',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.defaultDestinationAddress',
    title: `${t('driver.mostFrequentPath')} ${t('to')}`,
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: false,
    sortKey: ['defaultDestinationAddress.street', 'defaultDestinationAddress.cityName'],
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'defaultDestinationAddress.street',
              type: 'text',
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('to')}`,
              },
            },
            {
              key: 'defaultDestinationAddress.zip',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'defaultDestinationAddress.cityName',
              type: 'text',
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: false,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      key: 'bicycleType',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.frequency'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'driver.createdAt',
    title: t('createdAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      key: 'createdAt',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'driver.updatedAt',
    title: t('updatedAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      key: 'updatedAt',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'driver.amountOfCampaignRegistrations',
    title: t('driver.totalRegisteredCampaigns'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    filter: {
      key: 'amountOfCampaignRegistrations',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalRegisteredCampaigns'),
      },
    },
  },
  {
    key: 'driver.timestampLastRide',
    title: t('driver.lastRide'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      key: 'timestampLastRide',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.lastRide'),
      },
    },
  },
  {
    key: 'driver.endDateLastFinishedCampaign',
    title: t('driver.endDateLastFinishedCampaign'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      key: 'endDateLastFinishedCampaign',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.endDateLastFinishedCampaign'),
      },
    },
  },
  {
    key: 'driver.endDateLastRegisteredCampaign',
    title: t('driver.endDateLastRegisteredCampaign'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      key: 'endDateLastRegisteredCampaign',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.endDateLastRegisteredCampaign'),
      },
    },
  },
  {
    key: 'driver.totalDrivenDistance',
    title: t('driver.totalDistance'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      key: 'totalDrivenDistance',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>km</span>,
        label: t('driver.totalDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'driver.kcalSpent',
    title: t('driver.kcal'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'kcal',
    filter: {
      key: 'kcalSpent',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'driver.co2Saved',
    title: t('driver.co2'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'kg',
    filter: {
      key: 'co2Saved',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>co2</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'driver.totalDrivenTime',
    title: t('driver.totalTime') + ' (HH:MM:SS)',
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: number) => `${formatTimeDurationFromSeconds(v)}`,
    filter: {
      key: 'totalDrivenTime',
      type: 'duration',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalTime'),
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
      },
    },
  },
  {
    key: 'driver.avgSpeed',
    title: t('driver.averageSpeed'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'km/h',
    formatter: (v: number) => v ? `${(v*3.6).toFixed(2)} km/h` : '0 km/h',
    filter: {
      key: 'avgSpeed',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('driver.averageSpeed'),
        transformOut: (v: number) => v/3.6,
        transformIn: (v: number) => v*3.6,
      },
    },
  },
  {
    key: 'driver.numOfTours',
    title: t('driver.numOfTours'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    filter: {
      key: 'numOfTours',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfTours'),
      },
    },
  },
  {
    key: 'driver.numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    filter: {
      key: 'numOfDaysWithTour',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfDaysWithTour'),
      },
    },
  },
  {
    key: 'driver.numOfTopRatedPhotos',
    title: t('driver.numOfTopRatedPhotos'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    filter: {
      key: 'numOfTopRatedPhotos',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfTopRatedPhotos'),
      },
    },
  },
  {
    key: 'driver.totalPoints',
    title: t('driver.totalPoints'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: false,
    visible: true,
    filter: {
      key: 'totalPoints',
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.totalPoints'),
      },
    },
  },
  {
    key: 'driver.pointsLastWeek',
    title: t('driver.pointsLastWeek'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: false,
    visible: true,
    filter: {
      key: 'pointsLastWeek',
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.pointsLastWeek'),
      },
    },
  },
  {
    key: 'driver.rankLastWeek',
    title: t('driver.currentRank'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: false,
    visible: true,
    filter: {
      key: 'rankLastWeek',
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.currentRank'),
      },
    },
  },
  {
    key: 'driver.rankStatus',
    title: t('driver.statusEarned'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: false,
    visible: true,
    filter: {
      key: 'rankStatus',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        options: enumOptionsProvider('/enums/rankStatus'),
        label: t('driver.statusEarned'),
      },
    },
  },
  {
    key: 'driver.requestedLoyaltyPointReward',
    title: t('driver.requestedLoyaltyPointReward'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: false,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      key: 'requestedLoyaltyPointReward',
      type: 'bool',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.requestedLoyaltyPointReward'),
      },
    },
  },
  {
    key: 'driver.acceptedAgbVersions.privacy',
    title: t('driver.acceptedAgbVersions.privacy'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: item => item ?? 0,
    sortable: false,
    visible: false,
  },
  {
    key: 'driver.acceptedAgbVersions.registration',
    title: t('driver.acceptedAgbVersions.registration'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: item => item ?? 0,
    sortable: false,
    visible: false,
  },
  {
    key: 'driver.totalPaidOutPayout',
    title: t('driver.totalPaidOutPayout'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    formatter: (v: number) => v ? formatNumberForCurrency(v) : '',
    filter: {
      key: 'totalPaidOutPayout',
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
          label: t('driver.totalPaidOutPayout'),
          icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'driver.lastVerification',
    title: t('driver.lastVerification'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      key: 'lastVerification',
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.lastVerification'),
      },
    },
  },
  {
      key: 'driver.openCredits',
      title: t('driver.openCredits'),
      group: t('pages.driverList.fieldGroups.general'),
      sortable: false,
      visible: true,
      formatter: item => item === null ? '-' : formatNumberForCurrency(item),
      filter: {
        key: 'openCredits',
        type: 'range',
        group: t('pages.driverList.filterGroups.general'),
        options: {
            label: t('driver.openCredits'),
            icon: <span>CHF</span>,
        },
      },
  },
  {
      key: 'driver.openDebts',
      title: t('driver.openDebts'),
      group: t('pages.driverList.fieldGroups.general'),
      sortable: false,
      visible: true,
      formatter: item => item === null ? '-' : formatNumberForCurrency(item),
      filter: {
        key: 'openDebts',
        type: 'range',
        group: t('pages.driverList.filterGroups.general'),
        options: {
            label: t('driver.openDebts'),
            icon: <span>CHF</span>,
        },
      },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const { data: items, loading } = useProvider<HydraCollection<SegmentedDriver[]>>('/drivers/segmentation', providerParams);

const records = computed(() => unref(items)?.['hydra:member'] ?? []);
const total = computed(() => unref(items)?.['hydra:totalItems'] ?? 0);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const yesNoNull = (v?: boolean|null) => {
  if (v === undefined || v === null) return '';
  return v ? t('yes') : t('no');
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
